import React, { Fragment } from 'react';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import { VITALS_ACTIVITY_TYPE } from '../../constants';
import { PERIOD } from '../../../common/Charts/constants';
import InfoWrapper from '../../../common/Charts/InfoWrapper';
import { mapChartData, dataConverter } from '../../../common/Charts/helpers';

import ModalWrapper from '../../../common/Charts/ModalWrapper';
import ChartWrapper from '../../../common/Charts/ChartWrapper';
import ChartProvider from '../../../common/Charts/ChartProvider';
import HistoryWrapper from '../../../common/Charts/HistoryWrapper';
import { DEFAULTS, TIMELINE } from '../../../common/Charts/options';
import { TOOLTIP_VERTICAL_LINE } from '../../../common/Charts/plugins';
import * as respiratoryRateMeasurement from '../../../../redux/profiles/patientHealthboard/respiratoryRate-measurements-req';
import * as respiratoryRateMeasurementModal from '../../../../redux/profiles/patientHealthboard/respiratoryRate-measurements-modal-req';
import {
  useChartActions,
  useChartFormats,
  useHistoryActions,
} from '../../../common/Charts/hooks';
import {
  selectHistoricMeasurementsList,
  selectHistoricMeasurementsListPending,
  selectRespiratoryRateMeasurementEntity,
  selectRespiratoryRateMeasurementModalEntity,
  selectRespiratoryRateMeasurementEntityPending,
  selectRespiratoryRateMeasurementModalEntityPending,
} from '../../../../redux/profiles/patientHealthboard/selectors';

import { getCustomOptions } from './options';
import { calculateMinMaxRangeForRawValue } from '../../utils';
import { useCalculatePaddingYAxis } from '../useCalculatePaddingYAxis';

const { DAY, WEEK, MONTH } = PERIOD;
const { RESPIRATORY_RATE } = VITALS_ACTIVITY_TYPE;

const RespiratoryRateChart = ({ intl: { formatMessage } }) => {
  const historyData = useSelector(selectHistoricMeasurementsList);
  const periodData = useSelector(selectRespiratoryRateMeasurementEntity);
  const modalData = useSelector(selectRespiratoryRateMeasurementModalEntity);
  const historyLoading = useSelector(selectHistoricMeasurementsListPending);
  const periodLoading = useSelector(
    selectRespiratoryRateMeasurementEntityPending,
  );
  const modalLoading = useSelector(
    selectRespiratoryRateMeasurementModalEntityPending,
  );

  const formats = useChartFormats({ formatMessage });

  const { loadHistory } = useHistoryActions({ type: RESPIRATORY_RATE });

  const { isModal, openDay, closeDay, baseDate, loadPeriod } = useChartActions({
    actions: respiratoryRateMeasurement.actions,
    modalActions: respiratoryRateMeasurementModal.actions,
  });
  const metric = formatMessage({
    id: 'patient.healthboard.respiratory_rate.metric',
  });

  const paddingYAxisTicks = useCalculatePaddingYAxis();

  const setConfig = (data, date) => ({ period }) => {
    const { min, max } = calculateMinMaxRangeForRawValue(data);
    return {
      type: 'line',
      plugins: [TOOLTIP_VERTICAL_LINE],
      data: mapChartData(
        data,
        getCustomOptions({ period, date, formats, metric }),
      ),
      options: merge(DEFAULTS, TIMELINE[period], {
        layout: {
          padding: {
            top: isModal ? 120 : 140,
          },
        },
        tooltips: {
          enabled: [DAY, WEEK, MONTH].includes(period),
        },
        scales: {
          yAxes: [
            {
              ticks: {
                min,
                max,
                autoSkip: true,
                autoSkipPadding: paddingYAxisTicks,
                callback(value, _, allData) {
                  const roundedValue = Math.round(value);
                  if (allData.includes(roundedValue) && roundedValue !== value)
                    return null;
                  return roundedValue;
                },
              },
            },
          ],
        },
      }),
    };
  };

  const context = {
    isModal,
    mark: metric,
    converter: dataConverter.average,
    range: formatMessage({ id: 'patient.healthboard.average' }),
  };

  return (
    <Fragment>
      <ChartProvider extend={context}>
        <ChartWrapper
          onLoad={loadPeriod}
          loading={periodLoading}
          setConfig={setConfig(periodData)}
        />

        <InfoWrapper
          subtitle={formatMessage({ id: 'patient.healthboard.averageData' })}
          title={formatMessage({
            id: 'patient.healthboard.titleHistoric.all.respiratory_rate',
          })}
        >
          <HistoryWrapper
            unit="day"
            onClick={openDay}
            data={historyData}
            onLoad={loadHistory}
            loading={historyLoading}
          />
        </InfoWrapper>
      </ChartProvider>

      <ModalWrapper
        title={formatMessage({
          id: 'patient.healthboard.titleHistoric.respiratory_rate',
        })}
        open={isModal}
        onClose={closeDay}
      >
        <ChartProvider extend={context}>
          <ChartWrapper
            isModal
            loading={modalLoading}
            setConfig={setConfig(modalData, baseDate)}
            footer={<HistoryWrapper isModal data={modalData} />}
          />
        </ChartProvider>
      </ModalWrapper>
    </Fragment>
  );
};

RespiratoryRateChart.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(RespiratoryRateChart);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/TimeScalableChart/RespiratoryRateChart/index.jsx